import { useState } from "react";
import { Search, Trash2, Info } from "lucide-react";
import SetsTable from "./SetsTable";
import ExercisePickerDialog from "./ExercisePickerDialog";
import { ADVANCEMENT_TYPES, strengthSetDefaults, advancementGoalConfig } from "@/lib/workoutTaxonomy";

const inputCls =
    "w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red";

export default function LevelEditor({ category, level, onChange, onRemove, canRemove, testIdBase, validationError = null }) {
    const [pickerOpen, setPickerOpen] = useState(null);
    const exerciseMissing = !!validationError?.exerciseMissing;

    const update = (patch) => onChange({ ...level, ...patch });

    const onPickPrimary = (ex) => {
        const patch = { exercise_id: ex.exercise_id, exercise_name: ex.exercise_name };
        // In a strength workout, the exercise name tells us the rep target
        // (e.g. "5RM Goblet Box Squat"). Backfill empty set cells now so the
        // coach doesn't have to re-type defaults. Never overwrites values
        // the coach has already typed.
        const defaults = category === "strength" ? strengthSetDefaults(ex.exercise_name) : null;
        if (defaults && level.sets_data?.length) {
            patch.sets_data = level.sets_data.map((s) => ({
                ...s,
                percent_max: String(s.percent_max ?? "").trim() ? s.percent_max : defaults.percent_max,
                reps: String(s.reps ?? "").trim() ? s.reps : defaults.reps,
                rest_mins: String(s.rest_mins ?? "").trim() ? s.rest_mins : defaults.rest_mins,
            }));
        }
        onChange({ ...level, ...patch });
    };

    const onPickBasedOff = (ex) => update({ based_off_exercise_id: ex.exercise_id });
    const onPickAlt = (ex) => update({ alt_exercise_id: ex.exercise_id });

    return (
        <div data-testid={testIdBase} className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Exercise</div>
                {canRemove && (
                    <button type="button" onClick={onRemove} className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5">
                        <Trash2 className="w-3.5 h-3.5" /> Remove level
                    </button>
                )}
            </div>
            <button
                type="button"
                data-testid={`${testIdBase}-pick-exercise`}
                onClick={() => setPickerOpen("primary")}
                className={`w-full text-left bg-black border transition-colors px-3 py-3 flex items-center justify-between gap-3 ${exerciseMissing ? "border-brand-red ring-1 ring-brand-red/40" : "border-zinc-800 hover:border-brand-red"}`}
            >
                {level.exercise_id ? (
                    <span className="text-sm text-white">{level.exercise_name}</span>
                ) : (
                    <span className="text-sm text-zinc-300">Search and pick from the library…</span>
                )}
                <Search className="w-4 h-4 text-zinc-300 flex-shrink-0" />
            </button>

            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">Advancement type</label>
                    <select
                        value={level.advancement_type || ""}
                        onChange={(e) => update({ advancement_type: e.target.value || null })}
                        className={inputCls}
                    >
                        {ADVANCEMENT_TYPES.map((t) => <option key={t.value || "none"} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                {(() => {
                    // Goal field only renders when an advancement type is set.
                    // Label + placeholder adapt to the type so coaches see
                    // "Weight goal (lb)" or "Bodyweight multiplier goal", not
                    // an ambiguous "threshold value" with no unit hint.
                    const cfg = advancementGoalConfig(level.advancement_type);
                    if (!cfg) return null;
                    return (
                        <div>
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">{cfg.label}</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    step="0.05"
                                    placeholder={cfg.placeholder}
                                    value={level.advancement_value ?? ""}
                                    onChange={(e) => update({ advancement_value: e.target.value === "" ? null : Number(e.target.value) })}
                                    className={`${inputCls} pr-12`}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tracking-[0.18em] uppercase text-zinc-500 pointer-events-none">{cfg.unit}</span>
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-1.5 inline-flex items-start gap-1.5 leading-relaxed">
                                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                                <span>{cfg.help}</span>
                            </div>
                        </div>
                    );
                })()}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">Based off exercise (derived load)</label>
                    <button
                        type="button"
                        onClick={() => setPickerOpen("based_off")}
                        className="w-full text-left bg-black border border-zinc-800 hover:border-brand-red px-3 py-2 flex items-center justify-between gap-2"
                    >
                        <span className={`text-xs ${level.based_off_exercise_id ? "text-white" : "text-zinc-200"}`}>
                            {level.based_off_exercise_id || "None — uses this exercise's own max"}
                        </span>
                        <Search className="w-3.5 h-3.5 text-zinc-300" />
                    </button>
                </div>
                <div>
                    <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">Alt exercise (athlete may swap)</label>
                    <button
                        type="button"
                        onClick={() => setPickerOpen("alt")}
                        className="w-full text-left bg-black border border-zinc-800 hover:border-brand-red px-3 py-2 flex items-center justify-between gap-2"
                    >
                        <span className={`text-xs ${level.alt_exercise_id ? "text-white" : "text-zinc-200"}`}>
                            {level.alt_exercise_id || "None"}
                        </span>
                        <Search className="w-3.5 h-3.5 text-zinc-300" />
                    </button>
                </div>
            </div>

            <SetsTable
                sets={level.sets_data}
                onChange={(sets) => update({ sets_data: sets })}
                testIdBase={testIdBase}
                validationError={validationError && typeof validationError.setIdx === "number" ? validationError : null}
                category={category}
                exerciseName={level.exercise_name || ""}
            />

            <ExercisePickerDialog
                open={!!pickerOpen}
                category={category}
                onClose={() => setPickerOpen(null)}
                onPick={(ex) => {
                    if (pickerOpen === "primary") onPickPrimary(ex);
                    else if (pickerOpen === "based_off") onPickBasedOff(ex);
                    else if (pickerOpen === "alt") onPickAlt(ex);
                }}
            />
        </div>
    );
}
